import { useMemo } from 'react';
import chroma from 'chroma-js';
import { Layer, LayerProps } from 'shared/map-exports';
import { FeatureCollection } from 'geojson';
import { MapLayerProps } from 'features/mapping/mapping.types';
import { addVisible } from 'shared/addVisible';
import { OVERLAY_ICONS_BEFORE_ID } from 'features/mapping/styles';
import { ArcGisLayer } from '../ArcGisLayer';

const fields: string[] = ['ACQ_DATE', 'HOURS_OLD', 'CONFIDENCE'];

const colorStops0to24 = (() => {
  const scale = chroma
    .scale(['red', 'orange'])
    .domain([0, 24])
    .mode('lab')
    .correctLightness();
  const stops: (number | string)[] = [];
  for (let i = 0; i <= 24; i += 1) stops.push(i, scale(i).hex());
  return stops;
})();

const baseStyle: LayerProps = {
  id: 'modis',
  type: 'circle',
  filter: [
    'all',
    ['has', 'HOURS_OLD'],
    ['>=', ['get', 'HOURS_OLD'], 0],
    ['<=', ['get', 'HOURS_OLD'], 24],
  ],
  paint: {
    'circle-opacity': [
      'interpolate',
      ['linear'],
      ['zoom'],
      3,
      0.35,
      8,
      0.5,
      12,
      0.5,
      15,
      0.2,
      18,
      0.05,
    ],
    'circle-blur': 0.5,
    'circle-radius': [
      'interpolate',
      ['exponential', 2],
      ['zoom'],
      0,
      2,
      22,
      60000,
    ],
    'circle-color': [
      'interpolate',
      ['linear'],
      ['min', 24, ['max', 0, ['to-number', ['get', 'HOURS_OLD']]]],
      ...colorStops0to24,
    ],
  },
};

// pass-through, we rely on HOURS_OLD
const processData = (fc: FeatureCollection): FeatureCollection => fc;

export const ModisLayer = ({ visible }: MapLayerProps): JSX.Element => {
  const style = useMemo(() => addVisible(baseStyle, visible), [visible]);

  return (
    <ArcGisLayer
      visible={visible}
      src="https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/MODIS_Thermal_v1/FeatureServer/0"
      // Rolling window aligns with near-realtime
      where="HOURS_OLD < 24 AND CONFIDENCE >= 70"
      // If you need absolute time: ACQ_DATE BETWEEN TIMESTAMP '${rangeLow}' AND TIMESTAMP '${rangeHigh}'
      orderBy="ACQ_DATE DESC"
      fields={fields}
      precision={4}
      onData={processData}
      staleTime={60 * 60 * 1000} // 60 minutes
    >
      <Layer beforeId={OVERLAY_ICONS_BEFORE_ID} {...style} />
    </ArcGisLayer>
  );
};
